/** Original Ledzerly workflow content. Applicability is a documented auditor decision. */
import type { AuditQuery } from "./queries";
export const PACK_VERSION = "IN-2026.09-draft.1";
export const SOURCES = [
  ["Inflo workflow reference", "https://inflo.com/working-papers/"],
  [
    "ICAI auditing standards",
    "https://www.icai.org/post/auditing-review-and-other-standards-formerly-known-as-aas-complete-text",
  ],
  [
    "ICAI Schedule III guidance",
    "https://www.icai.org/post/icai-publications-corporate-laws-corporate-governance-committee",
  ],
  [
    "ICAI accounting frameworks",
    "https://www.icai.org/post/accounting-standards",
  ],
  [
    "ICAI SQC / SQM status",
    "https://www.icai.org/post/announcement-on-deferment-of-effective-date-of-sqm-1-and-sqm-2",
  ],
  [
    "ICAI CARO guidance hosted by CAG",
    "https://cag.gov.in/uploads/media/Guidance-note-on-CARO-2020-064e84867178ba9-47322653.pdf",
  ],
] as const;
export type Framework = "AS" | "Ind AS";
export type Stage =
  | "Acceptance"
  | "Planning"
  | "Fieldwork"
  | "Review"
  | "Completion"
  | "Archived";
export const STAGES: Stage[] = [
  "Acceptance",
  "Planning",
  "Fieldwork",
  "Review",
  "Completion",
  "Archived",
];
export type Decision = "unassessed" | "applicable" | "not-applicable";
export type Area =
  | "Acceptance"
  | "Planning"
  | "Financial statements"
  | "Substantive"
  | "Controls"
  | "Completion"
  | "CARO";
export interface Procedure {
  id: string;
  area: Area;
  title: string;
  standard: string;
  objective: string;
  evidence: string;
  conclusion: string;
  riskIds: string[];
  naReason: string;
  status: "open" | "prepared" | "reviewed";
  preparedBy?: string;
  preparedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNote?: string;
}
export interface Risk {
  id: string;
  title: string;
  assertion: string;
  level: "Low" | "Moderate" | "High" | "Significant";
  response: string;
}
export interface TbRow {
  code: string;
  name: string;
  current: number;
  prior: number;
  mapping: string;
}
export interface Finding {
  id: string;
  title: string;
  amount: number;
  severity: "Low" | "Moderate" | "High";
  disposition: "open" | "adjusted" | "unadjusted" | "reported";
  rationale: string;
}
export interface Request {
  id: string;
  title: string;
  owner: string;
  due: string;
  status: "requested" | "received" | "accepted" | "rejected";
  evidence: string;
  note: string;
}
export interface AuditFile {
  id: string;
  clientId: string;
  clientName: string;
  periodEnd: string;
  framework: Framework;
  entity: "company" | "nbfc" | "llp" | "other";
  consolidated: boolean;
  partner: string;
  stage: Stage;
  version: string;
  createdAt: string;
  updatedAt: string;
  applicability: Record<
    "caro" | "ifc" | "kam" | "auditTrail",
    { decision: Decision; rationale: string }
  >;
  frameworkRationale: string;
  acceptance: {
    independence: boolean;
    ethics: boolean;
    appointment: boolean;
    letterRef: string;
    predecessor: string;
  };
  materiality: {
    benchmark: string;
    base: number;
    percent: number;
    performancePercent: number;
    trivialPercent: number;
    rationale: string;
  };
  tb: TbRow[];
  tbSource: string;
  tbReconciled: boolean;
  risks: Risk[];
  procedures: Procedure[];
  requests: Request[];
  queries: AuditQuery[];
  findings: Finding[];
  completion: {
    opinion: string;
    reportDate: string;
    subsequentThrough: string;
    representationRef: string;
    goingConcern: string;
    opinionRationale: string;
    udin: string;
    qualityReview: string;
  };
  events: { at: string; actor: string; action: string }[];
}
export const newId = () => crypto.randomUUID();
const p = (
  id: string,
  area: Area,
  title: string,
  standard: string,
  objective: string,
): Procedure => ({
  id,
  area,
  title,
  standard,
  objective,
  evidence: "",
  conclusion: "",
  riskIds: [],
  naReason: "",
  status: "open",
});
export const CARO_TOPICS = [
  ["i", "PPE, intangibles, title deeds, revaluation and benami matters"],
  ["ii", "Inventory verification and working-capital statements"],
  ["iii", "Investments, guarantees, security, loans and advances"],
  ["iv", "Sections 185 and 186"],
  ["v", "Deposits and deemed deposits"],
  ["vi", "Cost records"],
  ["vii", "Statutory dues"],
  ["viii", "Previously unrecorded income"],
  ["ix", "Borrowings and use of funds"],
  ["x", "Public offers and private placements"],
  ["xi", "Fraud and whistle-blower matters"],
  ["xii", "Nidhi requirements"],
  ["xiii", "Related-party compliance"],
  ["xiv", "Internal audit"],
  ["xv", "Non-cash director transactions"],
  ["xvi", "RBI registration and core investment companies"],
  ["xvii", "Cash losses"],
  ["xviii", "Auditor resignation"],
  ["xix", "Liabilities falling due within one year"],
  ["xx", "Unspent CSR amounts"],
  ["xxi", "Group CARO qualifications"],
] as const;
export function programme(framework: Framework): Procedure[] {
  const ind = framework === "Ind AS";
  return [
    p(
      "A1",
      "Acceptance",
      "Engagement terms and independence",
      "SA 210 / SA 220",
      "Inspect appointment and signed terms; record independence threats, safeguards and predecessor communication.",
    ),
    p(
      "P1",
      "Planning",
      "Entity, systems and fraud discussion",
      "SA 240 / SA 315",
      "Understand revenue streams and systems; document fraud discussion and management override response.",
    ),
    p(
      "P2",
      "Planning",
      "Materiality and audit strategy",
      "SA 300 / SA 320",
      "Explain the selected benchmark, qualitative considerations and any lower thresholds for sensitive disclosures.",
    ),
    p(
      "P3",
      "Planning",
      "Data completeness and opening balances",
      "SA 500 / SA 510",
      "Reconcile the extracted trial balance to the books and prior signed statements; investigate migration differences.",
    ),
    p(
      "S1",
      "Substantive",
      "Revenue and cut-off",
      ind ? "Ind AS 115 · SA 330" : "AS 9 / AS 7 where relevant · SA 330",
      "Trace selected contracts to billing, performance and receipts. Challenge cut-off, variable amounts and deferred revenue.",
    ),
    p(
      "S2",
      "Substantive",
      "Receivables and recoverability",
      ind ? "Ind AS 109 · SA 505 / SA 540" : "AS framework · SA 505 / SA 540",
      "Control external confirmations; investigate exceptions and subsequent receipts. Evaluate the applicable impairment approach.",
    ),
    p(
      "S3",
      "Substantive",
      "Inventory",
      "AS 2 / Ind AS 2 · SA 501",
      "Document count attendance or alternatives; test ownership, costing, net realisable value and movement cut-off.",
    ),
    p(
      "S4",
      "Substantive",
      "Assets, CWIP and intangibles",
      ind ? "Ind AS 16 / 38 / 36" : "AS 10 / 26 / 28",
      "Test additions, title, capitalisation, useful lives, impairment and readiness for use; reconcile the asset register.",
    ),
    p(
      "S5",
      "Substantive",
      "Leases",
      ind ? "Ind AS 116" : "AS 19",
      "Evaluate contracts under the chosen framework; recalculate recognised balances and disclosures.",
    ),
    p(
      "S6",
      "Substantive",
      "Payables, accruals and MSME",
      "SA 330 / SA 500",
      "Search for unrecorded liabilities; inspect subsequent payments, supplier classification and applicable MSME disclosures.",
    ),
    p(
      "S7",
      "Substantive",
      "Cash, banks and borrowings",
      "SA 505 / SA 500",
      "Control bank confirmations; test reconciling items, restrictions, loan terms and covenant compliance.",
    ),
    p(
      "S8",
      "Substantive",
      "Employee benefits",
      ind ? "Ind AS 19 · SA 540" : "AS 15 · SA 540",
      "Reconcile payroll and employee data; assess actuarial inputs, liabilities and disclosures.",
    ),
    p(
      "S9",
      "Substantive",
      "Current and deferred tax",
      ind ? "Ind AS 12" : "AS 22",
      "Reconcile tax computations, timing or temporary differences as applicable, recognition support and disputed exposures.",
    ),
    p(
      "S10",
      "Substantive",
      "Provisions and contingencies",
      ind ? "Ind AS 37 · SA 501 / SA 540" : "AS 29 · SA 501 / SA 540",
      "Inspect legal correspondence and assumptions; assess recognition, measurement and omitted disclosures.",
    ),
    p(
      "S11",
      "Substantive",
      "Related parties",
      ind ? "Ind AS 24 · SA 550" : "AS 18 · SA 550",
      "Reconcile declarations, registers and ledger relationships; test approvals and disclosure completeness.",
    ),
    p(
      "S12",
      "Substantive",
      "GST, TDS and other statutory dues",
      "SA 250",
      "Reconcile books to returns and challans; assess unpaid dues, input-credit exceptions, RCM and financial-statement effects.",
    ),
    p(
      "S13",
      "Substantive",
      "Journal-entry testing",
      "SA 240",
      "Reconcile the journal population, define selection criteria and inspect support for unusual entries and management override.",
    ),
    p(
      "C1",
      "Controls",
      "Process controls and IT dependencies",
      "SA 315 / SA 330 / SA 402",
      "Document walkthroughs, control design and implementation. Test operating effectiveness where reliance is planned.",
    ),
    p(
      "F1",
      "Financial statements",
      "Presentation and disclosures",
      ind ? "Ind AS 1 / 7" : "AS 1 / 3",
      "Tie the statements and notes to the adjusted trial balance; assess the applicable presentation format and disclosure checklist.",
    ),
    p(
      "F2",
      "Financial statements",
      "Estimates and accounting policies",
      "SA 540 / SA 500",
      "Challenge methods, data and assumptions, including bias and consistency of accounting policies.",
    ),
    p(
      "E1",
      "Completion",
      "Misstatements and governance",
      "SA 450 / SA 260 / SA 265",
      "Evaluate individual and aggregate misstatements, qualitative matters and control deficiencies; document governance communication.",
    ),
    p(
      "E2",
      "Completion",
      "Subsequent events and going concern",
      "SA 560 / SA 570",
      "Evaluate events through the report date, management forecasts and uncertainty; document the reporting consequence.",
    ),
    p(
      "E3",
      "Completion",
      "Representations and report review",
      "SA 580 / SA 700 / SA 705 / SA 706",
      "Obtain appropriate representations and review the opinion, report wording and supporting evidence. Representations do not replace other evidence.",
    ),
    p(
      "E4",
      "Completion",
      "Comparatives and other information",
      "SA 710 / SA 720",
      "Check comparative information and read other information for material inconsistencies under the applicable requirements.",
    ),
  ];
}
export function createFile(
  input: Pick<
    AuditFile,
    | "clientId"
    | "clientName"
    | "periodEnd"
    | "framework"
    | "entity"
    | "partner"
    | "consolidated"
  >,
  actor: string,
): AuditFile {
  if (
    !input.clientName.trim() ||
    !input.partner.trim() ||
    !validDate(input.periodEnd)
  )
    throw new Error("Client, partner and a valid period end are required.");
  if (input.entity === "nbfc" && input.framework !== "Ind AS")
    throw new Error(
      "This initial NBFC pack supports Ind AS only; a sector-specific AS pack is not yet available.",
    );
  const now = new Date().toISOString();
  const decision = () => ({
    decision: "unassessed" as Decision,
    rationale: "",
  });
  return {
    ...input,
    id: newId(),
    stage: "Acceptance",
    version: PACK_VERSION,
    createdAt: now,
    updatedAt: now,
    frameworkRationale: "",
    applicability: {
      caro: decision(),
      ifc: decision(),
      kam: decision(),
      auditTrail: decision(),
    },
    acceptance: {
      independence: false,
      ethics: false,
      appointment: false,
      letterRef: "",
      predecessor: "",
    },
    materiality: {
      benchmark: "Profit before tax",
      base: 0,
      percent: 0,
      performancePercent: 0,
      trivialPercent: 0,
      rationale: "",
    },
    tb: [],
    tbSource: "",
    tbReconciled: false,
    risks: [],
    procedures: programme(input.framework),
    requests: [],
    queries: [],
    findings: [],
    completion: {
      opinion: "",
      reportDate: "",
      subsequentThrough: "",
      representationRef: "",
      goingConcern: "",
      opinionRationale: "",
      udin: "",
      qualityReview: "",
    },
    events: [{ at: now, actor, action: "Created draft engagement" }],
  };
}
export function presentation(file: Pick<AuditFile, "entity" | "framework">) {
  if (file.entity === "llp")
    return "LLP presentation: confirm applicable ICAI guidance";
  if (file.entity === "other")
    return "Non-corporate presentation: confirm applicable ICAI guidance";
  if (file.framework === "AS") return "Schedule III — Division I";
  return file.entity === "nbfc"
    ? "Schedule III — Division III"
    : "Schedule III — Division II";
}
export function validDate(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value
  );
}
export function amounts(m: AuditFile["materiality"]) {
  const overall = Math.round(m.base * m.percent) / 100;
  return {
    overall,
    performance: Math.round(overall * m.performancePercent) / 100,
    trivial: Math.round(overall * m.trivialPercent) / 100,
  };
}
export function materialityErrors(m: AuditFile["materiality"]): string[] {
  const errors: string[] = [];
  if (
    ![m.base, m.percent, m.performancePercent, m.trivialPercent].every(
      Number.isFinite,
    ) ||
    m.base <= 0 ||
    m.percent <= 0 ||
    m.percent > 100
  )
    errors.push(
      "Enter a positive benchmark and a percentage above zero and at most 100.",
    );
  if (m.performancePercent <= 0 || m.performancePercent >= 100)
    errors.push(
      "Performance materiality must be below overall materiality and above zero.",
    );
  if (m.trivialPercent <= 0 || m.trivialPercent >= m.performancePercent)
    errors.push(
      "Clearly trivial threshold must be below performance materiality and above zero.",
    );
  if (
    amounts(m).overall <= 0 ||
    amounts(m).performance <= 0 ||
    amounts(m).trivial <= 0
  )
    errors.push("Rounded materiality amounts must be positive.");
  if (!m.rationale.trim())
    errors.push(
      "Document the materiality rationale; no percentage is prescribed by this tool.",
    );
  return errors;
}
// CSV import is intentionally strict: malformed data never becomes zero silently.
export function readCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  let closed = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
          closed = true;
        }
      } else field += c;
    } else if (c === '"') {
      if (field || closed) throw new Error("Unexpected quote in CSV.");
      quoted = true;
    } else if (c === "," || c === "\n" || c === "\r") {
      row.push(field);
      field = "";
      closed = false;
      if (c !== ",") {
        if (c === "\r" && text[i + 1] === "\n") i++;
        if (row.some((v) => v.trim())) rows.push(row);
        row = [];
      }
    } else {
      if (closed && c.trim())
        throw new Error("Unexpected text after a quoted field.");
      if (!closed) field += c;
    }
  }
  if (quoted) throw new Error("Unclosed CSV quote.");
  row.push(field);
  if (row.some((v) => v.trim())) rows.push(row);
  return rows;
}
export function importTb(text: string): TbRow[] {
  if (text.length > 5_000_000) throw new Error("CSV limit is 5 MB.");
  const rows = readCsv(text.replace(/^\uFEFF/, ""));
  const header = rows.shift()?.map((x) => x.trim().toLowerCase());
  if (header?.join(",") !== "code,name,current,prior,mapping")
    throw new Error(
      "Expected columns: code,name,current,prior,mapping. Amounts are signed: debit positive, credit negative.",
    );
  if (rows.length > 10000)
    throw new Error("This evaluation import supports up to 10,000 accounts.");
  if (!rows.length) throw new Error("The trial balance has no account rows.");
  const codes = new Set<string>();
  const result = rows.map((r, i) => {
    if (r.length !== 5) throw new Error(`Row ${i + 2}: expected five columns.`);
    const [code, name, current, prior, mapping] = r.map((x) => x.trim());
    if (!code || !name || codes.has(code))
      throw new Error(
        `Row ${i + 2}: account code/name is missing or code is duplicated.`,
      );
    codes.add(code);
    if (
      ![current, prior].every(
        (v) =>
          /^-?\d+(\.\d{1,2})?$/.test(v) &&
          Number.isFinite(Number(v)) &&
          Math.abs(Number(v)) < 1e12,
      )
    )
      throw new Error(
        `Row ${i + 2}: amounts must be plain signed numbers, maximum two decimals, below 1 trillion.`,
      );
    return {
      code,
      name,
      current: Number(current),
      prior: Number(prior),
      mapping,
    };
  });
  return result;
}
export function tbSummary(rows: TbRow[]) {
  const cents = (key: "current" | "prior") =>
    rows.reduce((n, r) => n + Math.round(r[key] * 100), 0);
  return {
    currentDifference: cents("current") / 100,
    priorDifference: cents("prior") / 100,
    unmapped: rows.filter((r) => !r.mapping.trim()).length,
  };
}
export function syncConditionalProcedures(file: AuditFile): AuditFile {
  const extra: Procedure[] = [];
  if (file.applicability.caro.decision === "applicable")
    for (const [id, title] of CARO_TOPICS) {
      if (
        (file.consolidated && id !== "xxi") ||
        (!file.consolidated && id === "xxi")
      )
        continue;
      extra.push(
        p(
          `CARO-${id}`,
          "CARO",
          title,
          `CARO 2020 · 3(${id})`,
          "Assess each applicable subclause against the current Order; cross-reference supporting work and draft the reporting conclusion.",
        ),
      );
    }
  if (file.applicability.ifc.decision === "applicable")
    extra.push(
      p(
        "IFC",
        "Controls",
        "Internal financial controls reporting",
        "Companies Act · 143(3)(i)",
        "Determine reporting scope; assess financial-reporting controls, deficiencies and the separate reporting conclusion.",
      ),
    );
  if (file.applicability.auditTrail.decision === "applicable")
    extra.push(
      p(
        "TRAIL",
        "Controls",
        "Accounting software audit trail",
        "Audit and Auditors Rules · 11(g)",
        "Inventory relevant software; inspect edit-log operation, tampering, preservation and the reporting implication.",
      ),
    );
  if (file.applicability.kam.decision === "applicable")
    extra.push(
      p(
        "KAM",
        "Completion",
        "Key audit matters",
        "SA 701",
        "Determine matters requiring significant attention, communicate with governance and review the report description.",
      ),
    );
  if (file.consolidated)
    extra.push(
      p(
        "GROUP",
        "Financial statements",
        "Group audit and consolidation",
        "SA 600 · applicable AS / Ind AS",
        "Record component scope, other-auditor work, eliminations and group adjustments using the Indian requirements applicable to the engagement.",
      ),
    );
  const ids = new Set(extra.map((x) => x.id));
  const conditional = (id: string) =>
    id.startsWith("CARO-") || ["IFC", "TRAIL", "KAM", "GROUP"].includes(id);
  const retained = file.procedures.filter(
    (x) => !conditional(x.id) || ids.has(x.id),
  );
  return {
    ...file,
    procedures: [
      ...retained,
      ...extra.filter((x) => !retained.some((y) => y.id === x.id)),
    ],
  };
}
export function prepareProcedure(proc: Procedure, actor: string): Procedure {
  if (!actor.trim()) throw new Error("A preparer is required.");
  if (
    !proc.naReason.trim() &&
    (!proc.evidence.trim() || !proc.conclusion.trim())
  )
    throw new Error(
      "Evidence reference and conclusion are required, or document why the procedure is not applicable.",
    );
  return {
    ...proc,
    status: "prepared",
    preparedBy: actor,
    preparedAt: new Date().toISOString(),
    reviewedBy: undefined,
    reviewedAt: undefined,
  };
}
export function reviewProcedure(proc: Procedure, actor: string): Procedure {
  if (proc.status !== "prepared")
    throw new Error("Prepare this procedure before review.");
  if (
    !actor.trim() ||
    actor.trim().toLowerCase() === proc.preparedBy?.trim().toLowerCase()
  )
    throw new Error(
      "An independent reviewer must be different from the preparer.",
    );
  return {
    ...proc,
    status: "reviewed",
    reviewedBy: actor,
    reviewedAt: new Date().toISOString(),
  };
}
export function editProcedure(
  proc: Procedure,
  patch: Partial<
    Pick<
      Procedure,
      "evidence" | "conclusion" | "naReason" | "riskIds" | "reviewNote"
    >
  >,
): Procedure {
  return {
    ...proc,
    ...patch,
    status: "open",
    preparedBy: undefined,
    preparedAt: undefined,
    reviewedBy: undefined,
    reviewedAt: undefined,
  };
}
export function blockers(file: AuditFile, target: Stage): string[] {
  const list: string[] = [];
  const rank = STAGES.indexOf(target);
  const a = file.acceptance;
  if (rank >= 1) {
    if (
      !a.independence ||
      !a.ethics ||
      !a.appointment ||
      !a.letterRef.trim() ||
      !a.predecessor.trim()
    )
      list.push(
        "Complete acceptance checks, signed engagement-letter reference and predecessor communication / reason.",
      );
    if (!file.frameworkRationale.trim())
      list.push("Document why AS or Ind AS and the presentation format apply.");
    if (
      Object.values(file.applicability).some(
        (d) => d.decision === "unassessed" || !d.rationale.trim(),
      )
    )
      list.push(
        "Assess CARO, IFC, KAM and software audit-trail applicability with reasons.",
      );
    if (
      ["llp", "other"].includes(file.entity) &&
      ["caro", "ifc", "auditTrail"].some(
        (k) => file.applicability[k as "caro"].decision === "applicable",
      )
    )
      list.push(
        "Companies Act reporting packs require a company; review entity type and applicability.",
      );
  }
  if (rank >= 2) {
    list.push(...materialityErrors(file.materiality));
    const tb = tbSummary(file.tb);
    if (
      !file.tb.length ||
      tb.currentDifference !== 0 ||
      tb.priorDifference !== 0 ||
      tb.unmapped ||
      !file.tbReconciled ||
      !file.tbSource.trim()
    )
      list.push(
        "Import a balanced, mapped trial balance for both years and document source reconciliation.",
      );
    if (!file.risks.length || file.risks.some((r) => !r.response.trim()))
      list.push("Record assessed risks and audit responses.");
    if (
      file.risks.some(
        (r) =>
          !file.procedures.some(
            (p) => p.riskIds.includes(r.id) && !p.naReason.trim(),
          ),
      )
    )
      list.push("Link every risk to at least one applicable audit procedure.");
  }
  if (rank >= 3) {
    if (
      !file.procedures.length ||
      file.procedures.some((p) => p.status === "open")
    )
      list.push(
        "Prepare every procedure with evidence and conclusion, or a documented non-applicability reason.",
      );
    if (file.requests.some((r) => r.status !== "accepted"))
      list.push("Resolve and accept all client evidence requests.");
  }
  if (rank >= 4) {
    if (file.queries.some((q) => q.blocksCompletion && q.status !== "closed"))
      list.push(
        "Close all queries marked as completion blockers, including drafts, through independent review.",
      );
    if (
      file.procedures.some(
        (p) =>
          p.status !== "reviewed" ||
          !p.reviewedBy ||
          p.reviewedBy === p.preparedBy,
      )
    )
      list.push("Complete independent review of every procedure.");
    if (
      file.findings.some((f) => f.disposition === "open" || !f.rationale.trim())
    )
      list.push(
        "Evaluate all findings, including the reporting effect of unadjusted items.",
      );
  }
  if (rank >= 5) {
    const c = file.completion;
    if (
      !c.opinion ||
      !c.opinionRationale.trim() ||
      !c.representationRef.trim() ||
      !c.goingConcern.trim() ||
      !c.qualityReview.trim()
    )
      list.push(
        "Complete report conclusion, representations, going concern and quality-review disposition.",
      );
    if (!validDate(c.reportDate) || c.reportDate < file.periodEnd)
      list.push("The report date must be valid and not before the period end.");
    if (!validDate(c.subsequentThrough) || c.subsequentThrough < c.reportDate)
      list.push("Subsequent-events work must cover the report date.");
  }
  return list;
}
export function advance(file: AuditFile, actor: string): AuditFile {
  const next = STAGES[STAGES.indexOf(file.stage) + 1];
  if (!next) throw new Error("This engagement is already archived.");
  const errors = blockers(file, next);
  if (errors.length) throw new Error(errors.join(" "));
  return record({ ...file, stage: next }, actor, `Moved to ${next}`);
}
export function record(
  file: AuditFile,
  actor: string,
  action: string,
): AuditFile {
  const at = new Date().toISOString();
  return {
    ...file,
    updatedAt: at,
    events: [...file.events, { at, actor, action }],
  };
}
export function exportCsv(rows: (string | number)[][]): string {
  return rows
    .map((row) =>
      row
        .map((v) => {
          const s = String(v);
          const safe =
            typeof v === "string" && /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
          return `"${safe.replaceAll('"', '""')}"`;
        })
        .join(","),
    )
    .join("\r\n");
}
